/**
 * Bootstrap orchestrator for `aaac init --bootstrap`.
 */

import { existsSync } from "node:fs";
import { mkdir, writeFile } from "node:fs/promises";
import { join, resolve } from "node:path";
import { generateArtifactContractsYaml } from "./artifacts.js";
import { generateBindingTemplates } from "./bindings.js";
import { runArtifactDiscover } from "./discover.js";
import { generateProjectYaml } from "./manifest.js";
import { probeProject, type ProbeResult } from "./probe.js";
import { generateToolchainContractsYaml } from "./toolchain.js";
import { verifyBootstrap } from "./verify.js";

export type { ProbeResult } from "./probe.js";
export type { BootstrapReport } from "./verify.js";
export { probeProject } from "./probe.js";

export interface BootstrapOptions {
  projectRoot: string;
  projectYamlPath: string;
  noLlm?: boolean;
  artifactsOnly?: boolean;
  quiet?: boolean;
}

export interface BootstrapResult {
  probe: ProbeResult;
  generated: string[];
  reportPath: string;
}

async function writeIfChanged(absPath: string, content: string): Promise<boolean> {
  const { readFile, mkdir, writeFile } = await import("node:fs/promises");
  const { dirname } = await import("node:path");
  if (existsSync(absPath)) {
    const existing = await readFile(absPath, "utf8");
    if (existing === content) return false;
  }
  await mkdir(dirname(absPath), { recursive: true });
  await writeFile(absPath, content, "utf8");
  return true;
}

function rel(root: string, abs: string): string {
  return abs.startsWith(root + "/") ? abs.slice(root.length + 1) : abs;
}

export async function runBootstrap(options: BootstrapOptions): Promise<BootstrapResult> {
  const root = resolve(options.projectRoot);
  const projectYamlAbs = resolve(options.projectYamlPath);
  const artifactsOnly = Boolean(options.artifactsOnly);
  const noLlm = Boolean(options.noLlm);
  const quiet = Boolean(options.quiet);
  const generated: string[] = [];

  const probe = await probeProject(root);

  if (artifactsOnly && !existsSync(projectYamlAbs)) {
    throw new Error(
      `--artifacts-only requires an existing project.yaml at ${projectYamlAbs}. Run 'aaac init --bootstrap' first.`,
    );
  }

  // Phase 2 — artifact-contracts.yaml
  const artifactPath = join(root, "artifact-contracts.yaml");
  if (noLlm) {
    const yaml = generateArtifactContractsYaml(probe);
    await writeIfChanged(artifactPath, yaml);
    generated.push("artifact-contracts.yaml");
    if (!quiet) {
      process.stdout.write(`  created: artifact-contracts.yaml\n`);
    }
  } else {
    const discoverResult = await runArtifactDiscover(root);
    if (!discoverResult.success) {
      throw new Error(
        `artifact-contracts discover failed (exit ${discoverResult.exitCode}): ${discoverResult.stderr || discoverResult.stdout}`,
      );
    }
    generated.push("artifact-contracts.yaml");
    if (!quiet) {
      process.stdout.write(`  created: artifact-contracts.yaml (via discover)\n`);
    }
  }

  if (artifactsOnly) {
    const { reportPath } = await verifyBootstrap(root, projectYamlAbs, probe, {
      mode: "artifacts-only",
      noLlm,
      generated,
    });
    if (!quiet) {
      process.stdout.write(`  created: ${reportPath}\n`);
    }
    return { probe, generated, reportPath };
  }

  // Phase 3 — manifest + bindings + toolchain
  const projectYaml = generateProjectYaml(probe);
  await writeIfChanged(projectYamlAbs, projectYaml);
  generated.push(rel(root, projectYamlAbs));

  const toolchainPath = join(root, "toolchain-contracts.yaml");
  await writeIfChanged(toolchainPath, generateToolchainContractsYaml(probe));
  generated.push("toolchain-contracts.yaml");

  const bindings = generateBindingTemplates(probe);
  const bindingFiles: Array<[string, string]> = [
    ["bindings/git.yaml", bindings.git],
    ["bindings/runtime.yaml", bindings.runtime],
    ["bindings/observability.yaml", bindings.observability],
  ];
  for (const [relPath, content] of bindingFiles) {
    await writeIfChanged(join(root, relPath), content);
    generated.push(relPath);
  }

  if (!quiet) {
    for (const path of generated) {
      if (path !== "artifact-contracts.yaml") {
        process.stdout.write(`  created: ${path}\n`);
      }
    }
  }

  // Phase 4 — verify
  const { reportPath, report } = await verifyBootstrap(root, projectYamlAbs, probe, {
    mode: "full",
    noLlm,
    generated,
  });
  generated.push(reportPath);
  if (!quiet) {
    process.stdout.write(`  created: ${reportPath}\n`);
  }

  if (!report.verify.loadProjectConfig?.success) {
    throw new Error(
      `Bootstrap verify failed: loadProjectConfig — ${report.verify.loadProjectConfig?.error ?? "unknown error"}`,
    );
  }

  if (!quiet) {
    process.stdout.write(`Bootstrap complete at ${root}\n`);
  }

  return { probe, generated, reportPath };
}
